import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/Button';
import TextInput from '../components/TextInput';
import ErrorMessage from '../components/ErrorMessage';
import { logger } from '../utils/logger';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Get redirect URL from query params if it exists
  const queryParams = new URLSearchParams(location.search);
  const redirectUrl = queryParams.get('redirect') || '/';

  // Replace console.log and console.error calls
  logger.debug('Environment:', import.meta.env.MODE);
  logger.debug('API URL:', import.meta.env.VITE_API_URL);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      logger.debug('Attempting login with:', email);
      // Wait for login to complete and get user data
      await login(email, password);
      
      // Navigate to the redirect URL after successful login
      logger.debug('Login successful, redirecting to:', redirectUrl);
      setTimeout(()=>{
        navigate(redirectUrl);
        window.location.reload();
      }, 300);
    } catch (err: any) {
      logger.error('Login error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to login. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Login Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Welcome back
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {redirectUrl.startsWith('/invite/') 
                ? "Please sign in to accept your invitation"
                : "Sign in to manage your items and family groups"}
            </p>
          </div>
          
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="rounded-md space-y-6">
              <TextInput
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                label="Email"
                className="mb-6"
              />
              <TextInput
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                label="Password"
              />
            </div>

            {error && <ErrorMessage message={error} className="mt-6" />}

            <div className="mt-8">
              <Button
                type="submit"
                variant="primary"
                size="large"
                className="w-full text-xl font-medium"
                disabled={isLoading}
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </Button>
            </div>

            <div className="mt-6 text-center">
              <Link 
                to="/register" 
                className="inline-block font-medium text-indigo-600 hover:text-indigo-500 text-xl py-4 px-6"
              >
                Don't have an account? Register
              </Link>
            </div>
          </form>
        </div>
      </div>

      {/* Right side - Illustration */}
      <div className="hidden lg:flex lg:flex-1 bg-indigo-50 items-center justify-center p-12">
        <div className="max-w-2xl">
          <svg
            className="w-full h-auto"
            viewBox="0 0 1090 820"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="545" cy="410" r="320" fill="#E0E7FF" />
            <circle cx="545" cy="410" r="280" fill="#C7D2FE" />
            <circle cx="545" cy="410" r="240" fill="#A5B4FC" />
            <path
              d="M545 170v480M305 410h480"
              stroke="#6366F1"
              strokeWidth="4"
              strokeLinecap="round"
            />
            <path
              d="M425 290h240v240H425z"
              fill="#818CF8"
              fillOpacity="0.4"
            />
            <path
              d="M485 350h120v120H485z"
              fill="#6366F1"
              fillOpacity="0.6"
            />
            <circle cx="545" cy="410" r="40" fill="#4F46E5" />
            <path
              d="M545 170c132.548 0 240 107.452 240 240s-107.452 240-240 240"
              stroke="#4F46E5"
              strokeWidth="4"
              strokeLinecap="round"
            />
            <path
              d="M305 410c0-132.548 107.452-240 240-240"
              stroke="#6366F1"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray="16 16"
            />
          </svg>
          <h3 className="mt-8 text-2xl font-bold text-center text-indigo-900">
            Family Item Management
          </h3>
          <p className="mt-4 text-center text-indigo-700">
            Keep track of your family's belongings, share items, and stay organized together.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login; 